import { Controller } from "@hotwired/stimulus"

let printerQueue = Promise.resolve()

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export default class extends Controller {
  static targets = ["button", "rangeStart", "rangeEnd", "autoStatus"]
  static values = {voucher: Boolean, auto: Boolean}

  connect() {
    this.port = null
    this.disconnected = false
    if (this.autoValue) this.pollCafeOrders()
  }

  disconnect() {
    this.disconnected = true
    clearTimeout(this.pollTimer)
  }

  withPrinterLock(callback) {
    if (globalThis.navigator?.locks) return navigator.locks.request("liaf-receipt-printer", callback)
    const result = printerQueue.then(callback)
    printerQueue = result.catch(() => {})
    return result
  }

  async connectToSerialPort(interactive = true) {
    const previousPortIndex = localStorage.getItem('receiptSerialPortIndex')

    const ports = await navigator.serial.getPorts()

    if (previousPortIndex && ports[previousPortIndex]) {
      console.log(`기존 프린터 포트 사용:`, ports[previousPortIndex])
      return ports[previousPortIndex]
    } else {
      if (!interactive) throw new Error("자동 출력용 프린터를 다시 연결해 주세요.")
      console.log("새 포트를 선택해야 합니다.")
      return this.requestAndSavePort()
    }
  }

  async requestAndSavePort() {
    const port = await navigator.serial.requestPort()
    const ports = await navigator.serial.getPorts()

    const selectedIndex = ports.indexOf(port)
    localStorage.setItem('receiptSerialPortIndex', selectedIndex)

    console.log(`선택된 포트:`, port)
    return port
  }

  async cp949ArrayToPrinter(cp949array) {
    const writer = this.port.writable.getWriter()
    try {
      this.writeStarted = true
      await writer.write(cp949array)
    } finally {
      writer.releaseLock()
    }
  }

  async commandToPrinter(command) {
    const writer = this.port.writable.getWriter()
    let encodedCommand = new TextEncoder().encode(command)
    try {
      this.writeStarted = true
      await writer.write(encodedCommand)
    } finally {
      writer.releaseLock()
    }
  }

  // 9600 baud (8N1) = 960 bytes/sec → 전송 시간 계산 + 여유분
  waitForTransmit(byteLength) {
    const ms = Math.ceil(byteLength / 960 * 1000) * 2 + 500
    return sleep(ms)
  }

  async printVoucherRange(event) {
    event.preventDefault()

    const start = parseInt(this.rangeStartTarget.value, 10)
    const end = parseInt(this.rangeEndTarget.value, 10)

    if (isNaN(start) || isNaN(end)) {
      alert("시작/종료 번호가 유효하지 않습니다.")
      return
    }
    if (start > end) {
      alert("시작 번호가 종료 번호보다 클 수 없습니다.")
      return
    }

    this.element.dataset.voucherStart = start
    this.element.dataset.voucherEnd = end

    await this.printReceipt()
  }

  async printOrder(event) {
    return this.printReceipt(event, true)
  }

  async printReceipt(event, orderOnly = false) {
    event?.preventDefault()
    if (this.printing) return false
    this.printing = true
    try {
      return await this.withPrinterLock(() => this.printReceiptUnlocked(orderOnly))
    } finally {
      this.printing = false
    }
  }

  async printReceiptUnlocked(orderOnly = false, interactive = true) {
    this.writeStarted = false

    try {
      if (this.port === null) {
        this.port = await this.connectToSerialPort(interactive)
      }
      if (!this.port.readable || !this.port.writable) {
        await this.port.open({baudRate: 9600})
      }
    } catch (portError) {
      console.warn("포트 연결 실패:", portError)
      localStorage.removeItem('receiptSerialPortIndex')
      this.port = null
      if (interactive) alert("프린터 포트 연결 실패. 다시 클릭하면 포트 선택 창이 나타납니다.")
      return false
    }

    try {
      if (!orderOnly) {
        const paymentId = this.element.dataset.paymentId
        const cashOnly = this.element.dataset.cashOnly
        const rePrint = this.element.dataset.rePrint
        const cancel = this.element.dataset.cancel
        const voucherOnly = this.element.dataset.voucherOnly
        const single = this.element.dataset.single

        const voucherStart = this.element.dataset.voucherStart
        const voucherEnd = this.element.dataset.voucherEnd

        let url = `/api/print_receipt?payment_id=${paymentId}&cash_only=${cashOnly}&cancel=${cancel}&voucher_only=${voucherOnly}&re_print=${rePrint}`
        if (voucherStart && voucherEnd) {
          url += `&voucher_start=${voucherStart}&voucher_end=${voucherEnd}`
        }

        let response = await fetch(url, {
          method: "GET", headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute('content')
          }
        })

        if (!response.ok) throw new Error(`영수증 조회 실패 (${response.status})`)
        let data = await response.json()

        let store_info = new Uint8Array(data.store_info)
        let header = new Uint8Array(data.header)
        let items = new Uint8Array(data.items)
        let totals = new Uint8Array(data.totals)
        let total_summary = new Uint8Array(data.total_summary)
        let vat = new Uint8Array(data.vat)
        let use_voucher = new Uint8Array(data.use_voucher)
        let cash_receipt_info = new Uint8Array(data.cash_receipt_info)
        let card_info = new Uint8Array(data.card_info)
        let voucher_info = new Uint8Array(data.voucher_info)
        let footer = new Uint8Array(data.footer)
        let voucher = new Uint8Array(data.voucher)

        // 영수증 출력
        let receiptBytes = 3 // 행간 명령
        await this.commandToPrinter("\x1B\x33\x44")
        for (const section of [store_info, header, items, totals, vat, total_summary, use_voucher, cash_receipt_info, card_info, voucher_info, footer]) {
          await this.cp949ArrayToPrinter(section)
          receiptBytes += section.length
        }

        if (voucherOnly === "false") {
          await this.commandToPrinter("\x1B\x64\x05") // 줄바꿈
          await this.commandToPrinter("\x1D\x56\x01") // 커팅
          receiptBytes += 6
          // 영수증 데이터 전송 완료 대기
          await this.waitForTransmit(receiptBytes)
        }

        // 바우처 출력
        if (data.is_voucher === true) {
          if (voucherOnly !== "false" || cancel === "true") {
            await this.cp949ArrayToPrinter(voucher)
            await this.waitForTransmit(voucher.length)
          } else if (rePrint !== "true") {
            await this.cp949ArrayToPrinter(voucher)
            await this.waitForTransmit(voucher.length)
          }
        }
      }

      if (this.element.dataset.orderTicket) {
        const order = new Uint8Array(JSON.parse(this.element.dataset.orderTicket))
        await this.cp949ArrayToPrinter(order)
        await this.commandToPrinter("\x1B\x64\x05")
        await this.commandToPrinter("\x1D\x56\x01")
        await this.waitForTransmit(order.length + 6)
      }
      return true
    } catch (error) {
      console.error("프린트 오류:", error)
      localStorage.removeItem('receiptSerialPortIndex')
      if (interactive && this.element.dataset.orderTicket) alert("출력에 실패했습니다. 프린터와 실제 출력된 내용을 확인해 주세요.")
      return false
    } finally {
      if (this.port) {
        await sleep(2000)
        try {
          await this.port.close()
        } catch (closeErr) {
          console.warn("포트 닫기 실패:", closeErr)
        }
        this.port = null
      }
    }
  }

  async cafeRequest(after) {
    const query = after === null ? "" : `?after=${encodeURIComponent(after)}`
    const response = await fetch(`/pos/2/cafe_print${query}`, { cache: "no-store" })
    if (!response.ok) throw new Error("키오스크 주문 조회 실패")
    return response.json()
  }

  async enableCafePrinter() {
    try {
      await this.requestAndSavePort()
      this.autoPaused = false
      this.pollCafeOrders()
    } catch (error) {
      this.autoStatusTarget.textContent = error.message
    }
  }

  async pollCafeOrders() {
    if (this.polling || this.disconnected || !this.autoValue || this.autoPaused) return
    clearTimeout(this.pollTimer)
    this.polling = true
    try {
      if (!navigator.locks) throw new Error("자동 출력은 HTTPS의 Chrome/Edge에서 사용해 주세요.")
      await navigator.locks.request("liaf-receipt-printer", { ifAvailable: true }, async lock => {
        if (!lock || this.disconnected) return
        const cursor = localStorage.getItem("cafeLastPrintedOrder")
        if (cursor === null) {
          const initial = await this.cafeRequest(null)
          localStorage.setItem("cafeLastPrintedOrder", String(initial.last_id))
          this.autoStatusTarget.textContent = "키오스크 새 주문 자동 출력 대기 중"
          return
        }
        this.port = await this.connectToSerialPort(false)
        const data = await this.cafeRequest(cursor)
        for (const order of data.orders) {
          if (this.disconnected) return
          this.autoStatusTarget.textContent = `주문 ${order.id} 출력 중`
          this.element.dataset.orderTicket = JSON.stringify(order.bytes)
          const printed = await this.printReceiptUnlocked(true, false)
          delete this.element.dataset.orderTicket
          if (!printed) {
            this.autoPaused = true
            throw new Error(`주문 ${order.id} 출력 실패. 실제 출력 여부를 확인 후 프린터를 다시 연결해 주세요. 재시도 시 중복될 수 있습니다.`)
          }
          localStorage.setItem("cafeLastPrintedOrder", String(order.id))
        }
        this.autoStatusTarget.textContent = "키오스크 새 주문 자동 출력 대기 중"
      })
    } catch (error) {
      this.autoStatusTarget.textContent = error.message
    } finally {
      this.polling = false
      if (!this.disconnected && !this.autoPaused) this.pollTimer = setTimeout(() => this.pollCafeOrders(), 2000)
    }
  }
}
